/** Types and helpers shared by the Settings > Template Library tabs (KYC, AML, Risk). */

export type OnboardingKycClientType =
  | 'Limited'
  | 'Soletrader'
  | 'Partnership'
  | 'LLP'
  | 'Individual'
  | 'Trust'
  | 'Charity';

export interface OnboardingKycCriteriaRow {
  id: string;
  criteria: string;
  notes: string;
  yesNo: boolean;
  /** True for firm/global template rows (F_CompanyMaster = 0 and F_ParentUserId = 0) — not deletable. */
  isDefault?: boolean;
  /** Persisted display order (Settings > Template Library's drag-reorder). Null/missing rows
   *  (never explicitly ordered) sort after ordered ones, in their original API order. */
  orderId?: number | null;
}

/** Same shape as Onboarding & KYC; ids use `aml-` prefix to stay unique. */
export type AmlChecklistClientType = OnboardingKycClientType;

export type AmlChecklistCriteriaRow = OnboardingKycCriteriaRow;

/**
 * Risk Assessment client types extend the shared 7-type set with
 * 'PracticeRiskAssessment' (DB code 8) — a firm-level template category.
 * Does NOT appear in AML Checklist or Onboarding & KYC.
 */
export type RiskAssessmentClientType = OnboardingKycClientType | 'PracticeRiskAssessment';

export const ONBOARDING_KYC_CLIENT_TYPES: readonly OnboardingKycClientType[] = [
  'Limited',
  'Soletrader',
  'Partnership',
  'LLP',
  'Individual',
  'Trust',
  'Charity'
] as const;

export const TEMPLATE_CLIENT_TYPE_CODE: Record<OnboardingKycClientType, number> = {
  Soletrader: 1,
  Partnership: 2,
  LLP: 3,
  Individual: 4,
  Trust: 5,
  Charity: 6,
  Limited: 7
};

export const TEMPLATE_CLIENT_TYPE_CODE_ALIASES: Readonly<Record<number, number>> = {
  0: 7
};

export function templateClientTypeCode(t: OnboardingKycClientType): number {
  return TEMPLATE_CLIENT_TYPE_CODE[t];
}

export function templateClientTypeFromCode(code: number): OnboardingKycClientType | null {
  const normalised = TEMPLATE_CLIENT_TYPE_CODE_ALIASES[code] ?? code;
  for (const k of ONBOARDING_KYC_CLIENT_TYPES) {
    if (TEMPLATE_CLIENT_TYPE_CODE[k] === normalised) {
      return k;
    }
  }
  return null;
}

export const ONBOARDING_KYC_EXPORT_SEED: Record<OnboardingKycClientType, Omit<OnboardingKycCriteriaRow, 'id'>[]> = {
  Limited: [],
  Soletrader: [],
  Partnership: [],
  LLP: [],
  Individual: [],
  Trust: [],
  Charity: []
};

export function withOnboardingKycIds(
  type: OnboardingKycClientType,
  rows: Omit<OnboardingKycCriteriaRow, 'id'>[]
): OnboardingKycCriteriaRow[] {
  const slug = type.replace(/\s+/g, '-').toLowerCase();
  return rows.map((r, i) => ({ ...r, id: `${slug}-seed-${i}` }));
}

export function buildOnboardingKycSeedData(): Record<OnboardingKycClientType, OnboardingKycCriteriaRow[]> {
  const out = {} as Record<OnboardingKycClientType, OnboardingKycCriteriaRow[]>;
  for (const t of ONBOARDING_KYC_CLIENT_TYPES) {
    out[t] = withOnboardingKycIds(t, ONBOARDING_KYC_EXPORT_SEED[t]);
  }
  return out;
}

export const AML_CHECKLIST_CLIENT_TYPES = ONBOARDING_KYC_CLIENT_TYPES;

export const AML_CHECKLIST_EXPORT_SEED: Record<AmlChecklistClientType, Omit<AmlChecklistCriteriaRow, 'id'>[]> = {
  Limited: [],
  Soletrader: [],
  Partnership: [],
  LLP: [],
  Individual: [],
  Trust: [],
  Charity: []
};

export function withAmlChecklistIds(
  type: AmlChecklistClientType,
  rows: Omit<AmlChecklistCriteriaRow, 'id'>[]
): AmlChecklistCriteriaRow[] {
  const slug = type.replace(/\s+/g, '-').toLowerCase();
  return rows.map((r, i) => ({ ...r, id: `aml-${slug}-${i}` }));
}

export function buildAmlChecklistSeedData(): Record<AmlChecklistClientType, AmlChecklistCriteriaRow[]> {
  const out = {} as Record<AmlChecklistClientType, AmlChecklistCriteriaRow[]>;
  for (const t of AML_CHECKLIST_CLIENT_TYPES) {
    out[t] = withAmlChecklistIds(t, AML_CHECKLIST_EXPORT_SEED[t]);
  }
  return out;
}
